import { useState, type ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';
import { pad2 } from './dateCore';

// Static column-count classes kept as literals so Tailwind can see them.
const GRID_COLS: Record<number, string> = {
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  5: 'grid-cols-5',
  6: 'grid-cols-6',
  8: 'grid-cols-8',
};

const CHIP_BASE =
  'flex items-center justify-center rounded-phx border text-phx-xs font-semibold tabular-nums cursor-pointer transition-colors';
const CHIP_OFF = 'border-phx-border-2 bg-phx-surface text-phx-text-2 hover:bg-phx-surface-2';
const CHIP_ON = 'border-phx-primary bg-phx-primary-tint text-phx-primary';

function chipClass(on: boolean, compact: boolean): string {
  return cn(CHIP_BASE, on ? CHIP_ON : CHIP_OFF, compact ? 'h-phx-control-sm px-0.5' : 'h-6 px-1');
}

export interface TimeGridProps {
  hour: number | null;
  minute: number | null;
  /** Minute step (5 / 15 / 30). */
  step: number;
  /** Minute columns (2 or 4). */
  minuteCols: number;
  /** Hour columns — 6 (standalone TimeBox) or 8 (next to a calendar). */
  hourCols: number;
  minuteExpand: boolean;
  /** Compact cells for the calendar-adjacent grid. */
  compact?: boolean;
  /** Pixel width of the grid column. */
  width: number;
  /** Disabled (range "to" grid before its date is chosen). */
  enabled?: boolean;
  disabledHint?: string;
  hourLabel: string;
  minuteLabel: string;
  /** Optional heading above the grid (e.g. clock glyph + "Time from"). */
  caption?: ReactNode;
  selectMinuteLabel: string;
  anyMinuteLabel: string;
  onPickHour: (hour: number) => void;
  onPickMinute: (minute: number) => void;
}

/**
 * Time selection grid ("mřížka") shared by `TimeBox` and `DateBox`'s with-time panel: hours 0–23
 * on top, stepped minutes below, and an optional ▾ expander that opens the fine minutes within a
 * step. Ported from the design's `TimeField` / `DateField.timeGrid`.
 */
export function TimeGrid({
  hour,
  minute,
  step,
  minuteCols,
  hourCols,
  minuteExpand,
  compact = false,
  width,
  enabled = true,
  disabledHint,
  hourLabel,
  minuteLabel,
  caption,
  selectMinuteLabel,
  anyMinuteLabel,
  onPickHour,
  onPickMinute,
}: TimeGridProps) {
  const [expandedBase, setExpandedBase] = useState<number | null>(null);
  const expand = minuteExpand && step > 1;

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const bases: number[] = [];
  for (let m = 0; m < 60; m += step) bases.push(m);

  const subLabel = (text: string) => (
    <div className="text-phx-text-muted text-phx-3xs mb-1 font-semibold tracking-wide uppercase">
      {text}
    </div>
  );

  const hourGrid = (
    <div className={cn('gap-phx-1_5 grid', GRID_COLS[hourCols])}>
      {hours.map((h) => (
        <button
          key={`h${h}`}
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => onPickHour(h)}
          className={chipClass(h === hour, compact)}
        >
          {pad2(h)}
        </button>
      ))}
    </div>
  );

  let minuteGrid: ReactNode;
  if (!expand) {
    minuteGrid = (
      <div className={cn('gap-phx-1_5 grid', GRID_COLS[minuteCols])}>
        {bases.map((m) => (
          <button
            key={`m${m}`}
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onPickMinute(m)}
            className={chipClass(m === minute, compact)}
          >
            {pad2(m)}
          </button>
        ))}
      </div>
    );
  } else {
    minuteGrid = (
      <div className={cn('gap-phx-1_5 grid', GRID_COLS[minuteCols])}>
        {bases.map((base) => {
          const inBase = minute != null && minute >= base && minute < Math.min(base + step, 60);
          const display = inBase ? (minute as number) : base;
          const isOpen = expandedBase === base;
          return (
            <div
              key={`mb${base}`}
              className={cn(chipClass(inBase, compact), 'flex items-stretch overflow-hidden p-0')}
            >
              <button
                type="button"
                title={selectMinuteLabel}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  setExpandedBase(null);
                  onPickMinute(display);
                }}
                className="flex flex-1 items-center justify-center border-0 bg-transparent pr-px pl-1 font-semibold text-inherit tabular-nums"
              >
                {pad2(display)}
              </button>
              <button
                type="button"
                title={anyMinuteLabel}
                aria-label={anyMinuteLabel}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => setExpandedBase(isOpen ? null : base)}
                className={cn(
                  'text-phx-3xs flex w-[15px] flex-none items-center justify-center border-0 border-l opacity-80',
                  inBase ? 'border-phx-primary' : 'border-phx-border-2',
                  isOpen ? 'bg-phx-primary-tint' : 'bg-transparent'
                )}
              >
                <span
                  className={cn('inline-block transition-transform', isOpen && 'rotate-180')}
                  aria-hidden
                >
                  ▾
                </span>
              </button>
            </div>
          );
        })}
      </div>
    );
  }

  let subGrid: ReactNode = null;
  if (expand && expandedBase != null) {
    const fine: number[] = [];
    for (let x = expandedBase; x < expandedBase + step && x < 60; x++) fine.push(x);
    subGrid = (
      <div className="bg-phx-surface-2 border-phx-border rounded-phx p-phx-3_5 mt-1.5 border">
        <div className={cn('gap-phx-1_5 grid', GRID_COLS[minuteCols + 1])}>
          {fine.map((x) => (
            <button
              key={`s${x}`}
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                setExpandedBase(null);
                onPickMinute(x);
              }}
              className={chipClass(x === minute, compact)}
            >
              {pad2(x)}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      title={enabled ? undefined : disabledHint}
      className={cn('flex-none', !enabled && 'pointer-events-none opacity-40 grayscale-[0.5]')}
      style={{ width }}
    >
      {caption}
      {subLabel(hourLabel)}
      {hourGrid}
      <div className="border-phx-border pt-phx-3_5 mt-2 border-t">
        {subLabel(minuteLabel)}
        {minuteGrid}
        {subGrid}
      </div>
    </div>
  );
}
